import { Link } from 'react-router-dom';
import { ArrowRight, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Reveal } from '@/components/Reveal';
import { useCart } from '@/context/CartContext';
import { formatPrice } from '@/data/products';
import { useProducts } from '@/lib/shopify';

const catalogLabel = ({ loading, source }) => {
  if (loading) return 'Syncing';
  if (source === 'shopify') return 'Live Shopify';
  return 'Preview';
};

const DropCard = ({ product, index }) => {
  const { add, setOpen } = useCart();
  return (
    <Reveal delay={(index % 4) * 0.06}>
      <article className="group" data-testid={`drop-product-${product.id}`}>
        <div className="relative aspect-[3/4] overflow-hidden bg-[#F0F1EE]">
          <Link to={`/products/${product.id}`} data-testid={`drop-product-link-${product.id}`} className="block h-full">
            <img src={product.image} alt={product.name} loading="lazy" className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-[1.03]" />
          </Link>
          <span className="absolute left-3 top-3 border border-[#D1D1D6] bg-white/90 px-2 py-1 font-mono2 text-[8px] font-bold uppercase tracking-[0.2em] text-[#1E1E1E]">{product.tag}</span>
          <button
            data-testid={`drop-quick-add-${product.id}`}
            onClick={() => {
              add(product);
              toast.success(`${product.name} added to cart`);
              setOpen(true);
            }}
            className="absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center bg-[#1E1E1E] text-white transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-[#657260]"
            aria-label={`Quick add ${product.name}`}
          >
            <Plus size={17} />
          </button>
        </div>
        <div className="flex items-start justify-between gap-4 pt-4">
          <div>
            <Link to={`/products/${product.id}`} data-testid={`drop-product-name-link-${product.id}`} className="transition-colors hover:text-[#657260]"><h3 className="font-syne text-sm font-bold uppercase" data-testid={`drop-product-name-${product.id}`}>{product.name}</h3></Link>
            <p className="mt-1 font-mono2 text-[9px] uppercase tracking-[0.18em] text-[#777]">{product.color}</p>
          </div>
          <p className="font-mono2 text-xs text-[#5C5C5C]" data-testid={`drop-product-price-${product.id}`}>{formatPrice(product.price, product.currency)}</p>
        </div>
      </article>
    </Reveal>
  );
};

export const DropEdit = () => {
  const { products, source, loading } = useProducts();
  const featured = products.slice(0, 4);
  return (
    <section className="bg-[#F2F3F0]/85 px-5 py-24 text-[#1E1E1E] backdrop-blur-[2px] sm:px-8 lg:px-12 lg:py-36" data-testid="drop-edit-section">
      <div className="mx-auto max-w-[1500px]">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-7">
          <div>
            <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.28em] text-[#657260]" data-testid="home-catalog-status">
              Shop the edit / {catalogLabel({ loading, source })}
            </p>
            <h2 className="mt-4 font-syne text-4xl font-extrabold uppercase sm:text-5xl lg:text-6xl">Fresh on the rail.</h2>
          </div>
          <Link to="/shop" data-testid="drop-view-all" className="group flex items-center gap-3 border-b border-[#1E1E1E] pb-2 font-mono2 text-[10px] font-bold uppercase tracking-[0.24em]">
            View all pieces <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-12 md:grid-cols-4 md:gap-x-5">
          {featured.map((product, index) => <DropCard key={product.id} product={product} index={index} />)}
        </div>
      </div>
    </section>
  );
};